import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import axios from 'axios';
// Using native Date methods instead of date-fns for better compatibility

const API_URL = 'https://pdks.bytebridge.com.tr/api/projects';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: formatDate(new Date()),
    end_date: '',
    status: 'planning',
    priority: 'medium'
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL);
      setProjects(response.data);
    } catch (error) {
      showSnackbar('Projeler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(API_URL, formData);
      showSnackbar('Proje başarıyla eklendi', 'success');
      setOpenDialog(false);
      fetchProjects();
      resetForm();
    } catch (error) {
      showSnackbar('Proje eklenirken hata oluştu', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      start_date: formatDate(new Date()),
      end_date: '',
      status: 'planning',
      priority: 'medium'
    });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'on_hold':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const statusLabels = {
    planning: 'Planlama',
    in_progress: 'Devam Ediyor',
    on_hold: 'Beklemede',
    completed: 'Tamamlandı',
    cancelled: 'İptal Edildi'
  };

  const priorityLabels = {
    high: 'Yüksek',
    medium: 'Orta',
    low: 'Düşük'
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Projeler</Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => setOpenDialog(true)}
        >
          Yeni Proje Ekle
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Proje Adı</TableCell>
                <TableCell>Açıklama</TableCell>
                <TableCell>Başlangıç Tarihi</TableCell>
                <TableCell>Bitiş Tarihi</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Öncelik</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Yükleniyor...
                  </TableCell>
                </TableRow>
              ) : projects.length > 0 ? (
                projects.map((project, index) => (
                  <TableRow key={project.ID || project.id || `project-${index}`}>
                    <TableCell>{project.ProjectName}</TableCell>
                    <TableCell>{project.Description}</TableCell>
                    <TableCell>{new Date(project.StartDate).toLocaleDateString('tr-TR')}</TableCell>
                    <TableCell>{project.EndDate ? new Date(project.EndDate).toLocaleDateString('tr-TR') : '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={statusLabels[project.status] || project.status} 
                        color={getStatusColor(project.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={priorityLabels[project.priority] || project.priority} 
                        color={getPriorityColor(project.priority)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Henüz proje bulunmamaktadır.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Yeni Proje Ekle</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              name="name"
              label="Proje Adı"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="description"
              label="Açıklama"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={formData.description}
              onChange={handleInputChange}
            />
            <Box display="flex" gap={2} mt={2}>
              <TextField
                margin="dense"
                name="start_date"
                label="Başlangıç Tarihi"
                type="date"
                fullWidth
                variant="outlined"
                value={formData.start_date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
              <TextField
                margin="dense"
                name="end_date"
                label="Bitiş Tarihi"
                type="date"
                fullWidth
                variant="outlined"
                value={formData.end_date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
            <Box display="flex" gap={2} mt={2}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Durum</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Durum"
                  required
                >
                  <MenuItem key="planning" value="planning">Planlama</MenuItem>
                  <MenuItem key="in_progress" value="in_progress">Devam Ediyor</MenuItem>
                  <MenuItem key="on_hold" value="on_hold">Beklemede</MenuItem>
                  <MenuItem key="completed" value="completed">Tamamlandı</MenuItem>
                  <MenuItem key="cancelled" value="cancelled">İptal Edildi</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="dense">
                <InputLabel>Öncelik</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  label="Öncelik"
                  required
                >
                  <MenuItem key="high" value="high">Yüksek</MenuItem>
                  <MenuItem key="medium" value="medium">Orta</MenuItem>
                  <MenuItem key="low" value="low">Düşük</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>İptal</Button>
            <Button type="submit" variant="contained" color="primary">
              Kaydet
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Projects;
